import { Box, Icons, Link, palette, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import NearbyLocationCard from '~/components/ClinicDetailPageFlexible/components/BookModule/components/variants/NearbyBook/NearbyLocationCard';
import { NearbyLocations } from '~/components/ClinicDetailPageFlexible/ContextProviders';
import { analyticsTrackEvent } from '~/core/analytics';
import { CDP_NEARBY_PARTNER_MODULE_CLICKED } from '~/core/analytics/events';
import {
  FIND_MORE_LOCATIONS,
  NEARBY_MODULE_DESKTOP,
} from '~/components/ClinicDetailPageFlexible/components/BookModule/components/variants/NearbyBook/trackingUtils';
import dataTestIds from '~/test/test-ids';
import { FIND_PATH } from '~/constants';
import { remixUrl } from '~/core/remix';

type Props = {
  handleRequestAppointment: (location: LocationInterface, slot: Slot) => void;
  nearbyLocations?: NearbyLocations;
  nearbyVariation: string;
  setViewableSlots: React.Dispatch<React.SetStateAction<any>>;
  referringLocation: LocationInterface;
  visitTypeId?: string;
};

const DesktopNearbyContainer = ({
  handleRequestAppointment,
  nearbyLocations,
  setViewableSlots,
  referringLocation,
  nearbyVariation,
  visitTypeId,
}: Props) => {
  const hasNearbyLocations = !!nearbyLocations?.connect?.length;

  const handleFindMoreClick = () => {
    analyticsTrackEvent(CDP_NEARBY_PARTNER_MODULE_CLICKED, {
      referring_group_id_hash: referringLocation.groups[0].group_id,
      referring_location_id_hash: referringLocation.id,
      version: NEARBY_MODULE_DESKTOP,
      action: 'click',
      object: 'cta',
      object_type: FIND_MORE_LOCATIONS,
    });
  };

  return (
    <Box
      border="solid 1px"
      borderColor="gray-200"
      borderRadius="lg"
      data-testid={dataTestIds.cdp.nearbyBookModule}
      px={3}
      py={4}
      textColor="gray-600"
    >
      <Box
        bg="gray-100"
        border="solid 1px gray-100"
        borderRadius="sm"
        display="flex"
        mb={2}
        px={1}
        py={2}
        textColor="gray-500"
      >
        <Icons.AlertCircle color="gray-500" px={1} />
        <Text color="gray-500" variant="subtitle">
          This location is not bookable on Solv.
        </Text>
      </Box>
      <Text mb={2} variant="h3">
        Need care ASAP?
      </Text>
      {!nearbyLocations?.connect && (
        <Box display="flex" justifyContent="center" textColor="crunchberry">
          <Spinner color="crunchberry" size="2xl" />
        </Box>
      )}
      {nearbyLocations?.connect?.map((nearbyLocation: LocationInterface, index: number) => (
        <Stack key={nearbyLocation.id} mb={2}>
          <NearbyLocationCard
            handleRequestAppointmentClick={handleRequestAppointment}
            index={index}
            location={nearbyLocation}
            nearbyVariation={nearbyVariation}
            referringLocation={referringLocation}
            setViewableSlots={setViewableSlots}
            visitTypeId={visitTypeId}
          />
        </Stack>
      ))}
      {nearbyLocations?.connect && (
        <Box
          borderRadius="base"
          display="flex"
          justifyContent="center"
          p={1}
          sx={{
            background: hasNearbyLocations ? 'unset' : palette.crunchberry.base,
          }}
        >
          <Link
            external
            fontWeight={hasNearbyLocations ? 'regular' : 'medium'}
            href={remixUrl(FIND_PATH)}
            onClick={handleFindMoreClick}
            sx={{
              color: hasNearbyLocations
                ? `${palette.crunchberry.base} !important`
                : `${palette.egg} !important`,
              textDecoration: 'underline',
            }}
          >
            Find more bookable clinics
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default DesktopNearbyContainer;
